import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import {
  EnquiryConvertResponse,
  EnquiryDetail,
  EnquiryResolution,
  EnquiryResolveRequest,
  EnquiryStage,
  EnquirySummary,
  EnquiryTeamResolution,
} from '@/types/enquiry';
import { EnquiryExtractConvertRequest } from '@/types/extraction';

interface UseEnquiriesParams {
  page?: number;
  limit?: number;
  stage?: EnquiryStage;
  teamId?: string | null;
  resolutionCategory?: EnquiryResolution;
  autoFetch?: boolean;
}

export function useEnquiries(params: UseEnquiriesParams = {}) {
  const { page = 1, limit = 25, stage, teamId, resolutionCategory, autoFetch = true } = params;

  const [items, setItems] = useState<EnquirySummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchEnquiries = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await vnextApiClient.listEnquiries({
        page,
        limit,
        stage,
        team_id: teamId ?? undefined,
        resolution_category: resolutionCategory,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load enquiries');
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, stage, teamId, resolutionCategory]);

  useEffect(() => {
    if (autoFetch) {
      fetchEnquiries();
    }
  }, [autoFetch, fetchEnquiries]);

  return { items, total, isLoading, error, refetch: fetchEnquiries };
}

export function useEnquiry(enquiryId: string | undefined) {
  const [detail, setDetail] = useState<EnquiryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!enquiryId) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const result = await vnextApiClient.getEnquiry(enquiryId);
      setDetail(result);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load enquiry');
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [enquiryId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const extractConvert = useCallback(
    async (
      body: EnquiryExtractConvertRequest,
      append: boolean
    ): Promise<EnquiryConvertResponse | undefined> => {
      if (!enquiryId) {
        return undefined;
      }
      try {
        const result = await vnextApiClient.extractConvertEnquiryToRequests(
          enquiryId,
          body,
          append
        );
        await fetchDetail();
        notifications.show({
          title: append ? 'Report requests added' : 'Converted to ReportRequests',
          message: `${result.report_request_ids.length} ReportRequest(s) created.`,
          color: 'green',
        });
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Conversion failed');
        notifications.show({ title: 'Conversion failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [enquiryId, fetchDetail]
  );

  const resolve = useCallback(
    async (body: EnquiryResolveRequest) => {
      if (!enquiryId) {
        return;
      }
      try {
        const result = await vnextApiClient.resolveEnquiry(enquiryId, body);
        setDetail(result);
        notifications.show({
          title: 'Enquiry resolved',
          message: 'No ReportRequests were created.',
          color: 'green',
        });
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Resolve failed');
        notifications.show({ title: 'Resolve failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [enquiryId]
  );

  const setTeamId = useCallback(
    async (teamId: string | null): Promise<EnquiryDetail | undefined> => {
      if (!enquiryId) {
        return undefined;
      }
      try {
        const result = await vnextApiClient.updateEnquiry(enquiryId, { team_id: teamId });
        setDetail(result);
        notifications.show({
          title: 'Team updated',
          message: teamId ? `Assigned team ${teamId}.` : 'Cleared team assignment.',
          color: 'green',
        });
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Team update failed');
        notifications.show({ title: 'Team update failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [enquiryId]
  );

  /** Look the team up from the requester. An exact match is applied (and the
   * detail refreshed); a domain match comes back as a suggestion for the caller
   * to show. A failed lookup is shown as a message and resolves to `undefined`. */
  const resolveTeam = useCallback(async (): Promise<EnquiryTeamResolution | undefined> => {
    if (!enquiryId) {
      return undefined;
    }
    try {
      const result = await vnextApiClient.resolveEnquiryTeam(enquiryId);
      setDetail(result.enquiry);
      if (result.applied) {
        notifications.show({
          title: 'Team found',
          message: 'Matched the requester to their team.',
          color: 'green',
        });
      }
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Team lookup failed');
      notifications.show({ title: 'Team lookup failed', message: e.message, color: 'red' });
      return undefined;
    }
  }, [enquiryId]);

  return {
    detail,
    isLoading,
    error,
    refetch: fetchDetail,
    extractConvert,
    resolve,
    setTeamId,
    resolveTeam,
  };
}
